import authenticationBHSToken from "EstruturaAntiga/actions/authenticationBHSToken";
import UsuariosService from "services/UsuariosService";
import ContatosPortalDbService from "services/ContatosPortalDbService";
import DireitosPortalService from "services/DireitosPortalService";
import TenantsDynamicsService from "services/TenantsDynamicsService";
import photoDefault from "../assets/images/user-default.svg";
import { mapSessionStorageTenant } from "services/mappers/TenantMapper";
import NavegacoesService from "services/NavegacoesService";
import TermosAceiteService from "services/TermosAceiteService";
import DateUtils from "utils/date";
import { ehMesmoContexto } from "utils/contextos";

const AuthenticationService = {
    /**
     * Obtém o token armazenado no cache (sessionStorage) e verifica se ainda é válido.
     *
     * @returns {[boolean, object|null]} Um array onde:
     * - O primeiro elemento (`boolean`) indica se o token é válido.
     * - O segundo elemento (`object|null`) contém os dados do contato armazenado, ou `null` se o token for inválido.
     */
    ObterTokenBhsEmCache(){
        let cachedContact = JSON.parse(sessionStorage.getItem("contact"));
        let token = cachedContact?.tokenData;
        if(token && new Date(token?.expiration) > new Date(Date.now() + 2 * 60 * 1000)){
            return [true, cachedContact];
        }
        return [false, null];
    },
    /**
     * Obtém e retorna um novo token de autenticação do serviço BHS.
     *
     * @returns {Promise<{ sucesso: boolean, value: object|null, error: object|null }>}
     * Um objeto contendo:
     * - `sucesso` (`boolean`): Indica se a obtenção do token foi bem-sucedida.
     * - `value` (`object|null`): O token retornado pelo serviço ou `null` em caso de erro.
     * - `error` (`object|null`): Um objeto de erro em caso de falha, ou `null` se bem-sucedido.
     */
    async ObterNovoTokenBhs() {
        let error = null;
        let sucesso = false;
        const authData = await authenticationBHSToken().then((response)=>{
            if(response?.erros){
                error = {statuscode: 500, msg: response?.erros}
                return null;
            }
            sucesso = true;
            return response;
        }).catch(error =>{
            error = {statuscode: 401, msg: error}
            return null;
        })

        return [sucesso, authData, error];
    },
    /**
     * Atualiza os dados relacionados ao token, verificando primeiro se há um token válido no cache.
     * Se não houver, tenta obter um novo token e armazená-lo no sessionStorage.
     *
     * @returns {Promise<{ sucesso: boolean, value: object|null, error: object|null }>} 
     * Um objeto contendo:
     * - `sucesso` (`boolean`): Indica se a operação foi bem-sucedida.
     * - `value` (`object|null`): O token atualizado ou `null` caso não tenha sido obtido.
     * - `error` (`object|null`): Um objeto de erro caso a operação falhe, ou `null` se bem-sucedida.
     */
    async AtualizarEmCacheDadosRelacionadosAoToken(){
        const [cachedTokenSucesso, cachedToken] = this.ObterTokenBhsEmCache();
        if(cachedTokenSucesso){
            return [true, cachedToken, null];
        }
        let [sucesso, token, error] = await this.ObterNovoTokenBhs();
        if(sucesso){
            sessionStorage.setItem("contact", JSON.stringify(token));
        }
        return [sucesso, token, error];
    },
    async ObterContatoDoUsuario(authData){
        let error = null;
        let sucesso = false;
        let contatoResponse = await ContatosPortalDbService.ObterContato()
        .then(response=>{
            
                if(response.error || response.erros){
                    error = response.error || response.erros;
                    return null;
                }
                let contatoNovo = {...response,
                    tokenData: authData.tokenData,
                    message: authData.message,
                    authenticated: authData.authenticated,
                    userPhoto: photoDefault
                }
                sucesso = true;
                return contatoNovo;
            })
            .catch(requestError=>{
                error = {statuscode: requestError?.response?.status, msg: requestError?.response?.data}
                return null;
            });
        return [sucesso, contatoResponse, error]
    },
    async AtualizarEmCacheContatoDoUsuario(authData){
        let [sucesso, contatoResponse, error] = await this.ObterContatoDoUsuario(authData);
        if(sucesso){
            sessionStorage.setItem('contact', JSON.stringify(contatoResponse));
        }
        return [sucesso, contatoResponse, error];
    },
    async ListarContextosDoUsuario(){
        let error = null;
        let sucesso = false;
        let contextosResponse = await DireitosPortalService.ListarContextos().then(response=>{
            if(response?.error || response?.erros){
                error = response.error || response.erros;
                return null;
            }

            sucesso = true;
            return response;
        })
        .catch(requestError=>{
            error = {statuscode: requestError?.response?.status, msg: requestError?.response?.data}
            return null;
        });
        return [sucesso, contextosResponse, error]
    },
    async AtualizarEmCacheContextosDoUsuario(){
        let cachedContextos = JSON.parse(sessionStorage.getItem("contextos"));
        if(cachedContextos != null && cachedContextos != undefined){
            return [true, cachedContextos, null]
        }

        let [sucesso, contextosResponse, error] = await this.ListarContextosDoUsuario();
        if(sucesso){
            sessionStorage.setItem('contextos', JSON.stringify(contextosResponse));
        }

        return [sucesso, contextosResponse, error];
    },
    ObterContextoAtualEmCache(){
        const contextoSelecionado = this.ObterContextoSelecionadoEmCache();
        const contextos = JSON.parse(sessionStorage.getItem("contextos")) ?? [];
        return contextos.find(contexto => ehMesmoContexto(contexto, contextoSelecionado)) ?? null;
    },
    ObterContextoSelecionadoEmCache(){
        return JSON.parse(sessionStorage.getItem("contexto"));
    },
    SalvarContextoSelecionadoEmCache(contexto){
        sessionStorage.setItem("contexto", JSON.stringify({ clienteId: contexto.clienteId, tenantId: contexto.tenantId ?? null }));
    },
    async ObterTenantAtualDoUsuario(tenantId){
        let error = null;
        let sucesso = false;
        let tenant = await TenantsDynamicsService.ObterTenant(tenantId)
        .then(response=>{
            if(response.error || response.erros){
                error = response.error || response.erros;
                return null;
            }

            sucesso = true;
            return response;
        })
        .catch(requestError=>{
            error = {statuscode: requestError?.response?.status, msg: requestError?.response?.data}
            return null;   
        });
        return [sucesso, tenant ? mapSessionStorageTenant(tenant) : null, error]
    },
    /**
     * Atualiza o cache do tenant atual do usuário, caso ainda não esteja armazenado.
     *
     * @param {string} tenantId - O ID do tenant a ser atualizado no cache.
     * @returns {Promise<{ sucesso: boolean, tenant: object|null, erro: object|null }>} 
     * Um objeto contendo:
     * - `sucesso` (`boolean`): Indica se a operação foi bem-sucedida.
     * - `tenant` (`object|null`): Dados do tenant, se encontrados.
     * - `erro` (`object|null`): Detalhes do erro, caso ocorra.
     */
    async AtualizarEmCacheTenantAtual(tenantId, ignorarValorEmCache = false){
        let cachedTenant = JSON.parse(sessionStorage.getItem("tenant"));
        if(cachedTenant != null && cachedTenant != undefined && !ignorarValorEmCache){
            return [true, cachedTenant, null]
        }
        if(tenantId == null){
            sessionStorage.setItem('tenant', JSON.stringify({}));
            return [true, {}, null]
        }

        let [sucesso, tenantResponse, error] = await this.ObterTenantAtualDoUsuario(tenantId);
        if(sucesso){
            sessionStorage.setItem('tenant', JSON.stringify(tenantResponse));
        }
        return [sucesso, tenantResponse,error];
    },
    async ObterDadosPerfilUsuario(userData){
        let error = null;
        let sucesso = false;

        let perfilResponse = await Promise.all([
            UsuariosService.ObterUsuario(userData.profile.upn, userData.profile.tid),
            UsuariosService.ObterFoto(userData.profile.upn, userData.profile.tid)
        ]).then(([usuarioAAD, foto])=>{
            
            sucesso = true;
            return{
                onPremisesSyncEnabled: usuarioAAD?.sincOnPremisesHabilitado,
                userPhoto: (!foto || foto.error) ? photoDefault : 'data:image/jpeg;base64,' + foto
            }
        }).catch((responseError)=>{
            error = responseError;
            return null;
        })
        return [sucesso, perfilResponse, error]
    }
    ,
    async AtualizarEmCacheContatoComPerfilDoUsuario(userData){
        let cachedContact = JSON.parse(sessionStorage.getItem("contact"));
        
        let [sucesso, perfil, error] = await this.ObterDadosPerfilUsuario(userData);
        let contatoAtualizado = {
            ...cachedContact,
            onPremisesSyncEnabled: perfil?.onPremisesSyncEnabled,
            userPhoto: perfil?.userPhoto
        }
        sessionStorage.setItem("contact", JSON.stringify(contatoAtualizado));

        return [sucesso, contatoAtualizado,error]
    },
    async ObterNavegacoesUsuario(){
        let error = null;
        let sucesso = false;
        let navegacaoResponse = await NavegacoesService.ListarNavegacoesPorUsuario().then(response=>{
            if(response?.error || response?.erros){
                error = response.error || response.erros;
                return null;
            }

            sucesso = true;
            return response;
        })
        .catch(requestError=>{
            error = {statuscode: requestError.response.status, msg: requestError.response.data}
            return null;
        });
        return [sucesso, navegacaoResponse, error]
    },
    async AtualizarEmCacheNavegacoesDoUsuario(ignorarValorEmCache = false){
        let navegacaoEmCache = JSON.parse(localStorage.getItem("navegacoes"));
        if(navegacaoEmCache != null && navegacaoEmCache != undefined && !ignorarValorEmCache){
            const agora = new Date().getTime();
            const timestampCache = navegacaoEmCache.timestamp;
            if (agora - timestampCache < DateUtils.GetDaysInMs(1)) {
                return [true, navegacaoEmCache.dados, null];
            }
        }
        let [sucesso, navegacaoResponse, error] = await this.ObterNavegacoesUsuario();
        if(sucesso){
            localStorage.setItem('navegacoes', JSON.stringify({
                timestamp: new Date().getTime(),
                dados: navegacaoResponse
            }));
        }
        return [sucesso, navegacaoResponse, error];
    },
    async ObterTermosAceite(){
        let error = null;
        let sucesso = false;
        let termosAceiteResponse = await TermosAceiteService.ListarTermosAceite().then(response=>{
            if(response?.error || response?.erros){
                error = response.error || response.erros;
                return null;
            }
            sucesso = true;
            return response;
        })
        .catch(requestError=>{
            error = {statuscode: requestError.response.status, msg: requestError.response.data}
            return null;
        });
        return [sucesso, termosAceiteResponse, error]
    },
    async AtualizarEmCacheTermosAceite(ignorarValorEmCache = false){
        let termosAceiteEmCache = JSON.parse(sessionStorage.getItem("termosAceite"));
        if(termosAceiteEmCache != null && termosAceiteEmCache != undefined && !ignorarValorEmCache){
            return [true, termosAceiteEmCache, null]
        }
        let [sucesso, termosAceiteResponse, error] = await this.ObterTermosAceite();
        if(sucesso){
            sessionStorage.setItem('termosAceite', JSON.stringify(termosAceiteResponse));
        }
        return [sucesso, termosAceiteResponse, error];
    },

}

export default AuthenticationService;